class CustomNavDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetails = this.querySelector('details');
    this.mainSummary = this.mainDetails.querySelector('summary');
    this.drawer = this.mainDetails.querySelector('.custom-nav-drawer');
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.mainSummary.addEventListener('click', this.onSummaryClick.bind(this));
    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    window.addEventListener('resize', this.setViewportMetrics.bind(this));

    this.accordions = Array.from(this.querySelectorAll('.custom-nav-drawer__accordion'));
    this.accordions.forEach((accordion) => {
      const summary = accordion.querySelector('summary');
      summary.setAttribute('role', 'button');
      summary.setAttribute('aria-expanded', accordion.hasAttribute('open'));
      accordion.addEventListener('toggle', this.onAccordionToggle.bind(this, accordion));
    });

    // global.js attaches onKeyUpEscape to every details outside header-drawer/menu-drawer;
    // it would close our main details abruptly, bypassing close()'s body-lock/focus cleanup.
    if (typeof onKeyUpEscape === 'function') {
      this.querySelectorAll('details').forEach((details) => details.removeEventListener('keyup', onKeyUpEscape));
    }
  }

  get isOpen() {
    return this.mainDetails.hasAttribute('open');
  }

  // The drawer is positioned below the header (top: 100%) and sized from these
  // two variables — the same contract Dawn's HeaderDrawer maintains.
  setViewportMetrics() {
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    const header = document.querySelector('.section-header');
    if (header) {
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(header.getBoundingClientRect().bottom)}px`,
      );
    }
  }

  onSummaryClick(event) {
    event.preventDefault();
    this.isOpen ? this.close() : this.open();
  }

  open() {
    this.setViewportMetrics();
    this.cancelPendingClose();
    this.mainDetails.setAttribute('open', '');
    // setTimeout rather than requestAnimationFrame: rAF never fires in background
    // tabs, which would leave the drawer open-but-untranslated (Dawn's MenuDrawer
    // uses the same idiom).
    setTimeout(() => {
      this.mainDetails.classList.add('menu-opening');
    });
    this.mainSummary.setAttribute('aria-expanded', true);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
    document.querySelector('.section-header')?.classList.add('menu-open');
    if (typeof trapFocus === 'function') trapFocus(this.drawer, this.mainSummary);
  }

  close() {
    if (!this.isOpen) return;
    const hadTransition = this.mainDetails.classList.contains('menu-opening');
    this.mainDetails.classList.remove('menu-opening');

    const finish = () => {
      this.mainDetails.removeAttribute('open');
    };
    if (this.reducedMotion.matches || !hadTransition) {
      finish();
    } else {
      // Not {once: true}: the visibility transition fires transitionend too and
      // would consume the listener before the transform event arrives. A reopen
      // mid-close cancels via cancelPendingClose().
      this.pendingClose = (event) => {
        if (event.target !== this.drawer || event.propertyName !== 'transform') return;
        this.cancelPendingClose();
        finish();
      };
      this.drawer.addEventListener('transitionend', this.pendingClose);
      // Fallback: transitionend never fires in background tabs or when the
      // transition is cancelled; without it the details would stay [open] and
      // the next summary click would close instead of open.
      this.pendingCloseTimeout = setTimeout(() => {
        if (!this.pendingClose) return;
        this.cancelPendingClose();
        finish();
      }, 500);
    }

    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    document.querySelector('.section-header')?.classList.remove('menu-open');
    if (typeof removeTrapFocus === 'function') removeTrapFocus(this.mainSummary);
    this.mainSummary.setAttribute('aria-expanded', false);
  }

  cancelPendingClose() {
    if (!this.pendingClose) return;
    this.drawer.removeEventListener('transitionend', this.pendingClose);
    this.pendingClose = null;
    clearTimeout(this.pendingCloseTimeout);
  }

  onAccordionToggle(accordion) {
    const summary = accordion.querySelector('summary');
    summary.setAttribute('aria-expanded', accordion.hasAttribute('open'));
    if (!accordion.hasAttribute('open')) return;
    this.accordions.forEach((other) => {
      if (other !== accordion) other.removeAttribute('open');
    });
  }

  onKeyUp(event) {
    if (event.code !== 'Escape') return;
    const openAccordion = event.target.closest('.custom-nav-drawer__accordion[open]');
    if (openAccordion) {
      event.stopPropagation();
      openAccordion.removeAttribute('open');
      openAccordion.querySelector('summary').focus();
      return;
    }
    this.close();
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.isOpen && !this.contains(document.activeElement)) this.close();
    });
  }
}

customElements.define('custom-nav-drawer', CustomNavDrawer);

/*
 * Desktop hover-to-open for the header's top-level menus.
 *
 * Stock Dawn is click-only: details-disclosure.js binds nothing but `focusout` and
 * `toggle`, and the native <summary> click does the opening. This adds hover on
 * pointer-capable desktop viewports without touching Dawn's JS or the markup
 * contract — it only sets/clears `open` on the same <details>, so Dawn's toggle
 * handler still runs (panel animation, sticky-header preventHide) and click and
 * keyboard keep working unchanged.
 *
 * Delegated from the document so a Theme-Editor section re-render needs no re-init.
 * Covers both desktop menu types: the mega snippet and Dawn's dropdown snippet render
 * the same <nav class="header__inline-menu"> … <header-menu><details> contract.
 */
(() => {
  const DESKTOP_HOVER = window.matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)');
  const MENU_SELECTOR = '.header__inline-menu header-menu > details';
  const CLOSE_DELAY = 125;

  let closeTimer = null;
  // A menu the pointer is still sitting on after a click closed it. Without this it
  // reopens on the next mouseover, making the summary look like it ignores clicks.
  let suppressed = null;

  const openMenus = () => Array.from(document.querySelectorAll(`${MENU_SELECTOR}[open]`));

  function closeMenus(except) {
    openMenus().forEach((details) => {
      if (details === except) return;
      const menu = details.parentElement;
      // Prefer Dawn's own close() — it clears aria-expanded too. The fallback covers
      // the window where the custom element is still waiting on deferred JS.
      if (typeof menu.close === 'function') {
        menu.close();
      } else {
        details.removeAttribute('open');
      }
    });
  }

  function cancelClose() {
    if (closeTimer === null) return;
    clearTimeout(closeTimer);
    closeTimer = null;
  }

  function scheduleClose() {
    if (closeTimer !== null || !openMenus().length) return;
    closeTimer = setTimeout(() => {
      closeTimer = null;
      closeMenus(null);
    }, CLOSE_DELAY);
  }

  document.addEventListener('mouseover', (event) => {
    if (!DESKTOP_HOVER.matches) return;

    const target = event.target instanceof Element ? event.target : null;
    const details = target ? target.closest(MENU_SELECTOR) : null;
    if (details !== suppressed) suppressed = null;

    // The panel sits at the header's bottom edge, so the pointer crosses the header's
    // own padding — inside neither the summary nor the panel — on the way down to it.
    // Closing on a delay is what stops the panel flickering shut mid-travel.
    if (!target || !target.closest('.header__inline-menu')) {
      scheduleClose();
      return;
    }

    cancelClose();
    closeMenus(details);
    if (details && details !== suppressed && !details.open) details.open = true;
  });

  document.addEventListener('click', (event) => {
    if (!DESKTOP_HOVER.matches) return;

    const target = event.target instanceof Element ? event.target : null;
    const summary = target ? target.closest(`${MENU_SELECTOR} > summary`) : null;
    if (!summary) return;

    // Runs before the native toggle, so `open` is still the pre-click state: an open
    // menu is on its way closed and must not reopen until the pointer leaves it.
    const details = summary.parentElement;
    suppressed = details.open ? details : null;
  });
})();

/*
 * Warm the desktop panel images.
 *
 * Every image in a panel — nav-item thumbnails, product cards, the promo — is
 * `loading="lazy"`, and a closed <details> doesn't render its subtree, so the lazy
 * heuristic doesn't reach them until well after load. Open a menu inside that window
 * and the panel appears with empty image slots. Hover makes it far easier to hit,
 * because a menu can now open the moment the pointer crosses it.
 *
 * Promoting them to eager on idle fetches them ahead of the first open without
 * competing with the page's own LCP. Scoped to `.header__inline-menu`, which Dawn
 * hides below 990px, so a phone never pays for images it has no way to show.
 */
(() => {
  const DESKTOP = window.matchMedia('(min-width: 990px)');
  let warmed = false;

  function warmPanelImages() {
    if (warmed || !DESKTOP.matches) return;
    warmed = true;
    document.querySelectorAll('.header__inline-menu img[loading="lazy"]').forEach((image) => {
      image.loading = 'eager';
    });
  }

  function schedule() {
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(warmPanelImages, { timeout: 3000 });
    } else {
      setTimeout(warmPanelImages, 1000);
    }
  }

  if (document.readyState === 'complete') {
    schedule();
  } else {
    window.addEventListener('load', schedule, { once: true });
  }

  // Started narrow, then widened past the breakpoint — the panels are reachable now.
  DESKTOP.addEventListener('change', schedule);
})();
